/**
 * Product finder (sections/ccc-product-finder.liquid), first used as the
 * Filter finder on /collections/coffee-filters.
 *
 * Step 1 (brewer) -> step 2 (size) -> results. Liquid renders the step 1 tiles
 * and a JSON island with every product (finder tags, variants, prices, stock,
 * pre-order) plus a config block. This module filters that data, draws the
 * result cards and keeps the choice in the URL so it can be shared and the
 * Back button walks through the steps.
 *
 *   ?brewer=v60&size=02&type=paper   (parameter names come from the section)
 *
 * Tags it reads (prefixes come from the section settings):
 *   tile tags ("Fits <Brewer>", "Espresso Papers") are read off the tiles'
 *                          data-tag; a product can carry several
 *   toggle tag             "Reusable Filters": second toggle option
 *   size prefix            "Filter Size: <size>": step 2; products without
 *                          one show under "Not sure" only
 * Products with no tile tag at all land under "Something else".
 * Internal names stay brewer/size whatever the finder is about.
 *
 * Add to cart uses the same CartLinesUpdateEvent + promise flow as Horizon's
 * product form, so the header bubble, cart drawer (when the store uses one)
 * and CCC cart listeners all refresh. It only adds plain in-stock variants
 * that match the chosen size exactly; pre-orders and multi-option products
 * go to the product page so the shopper sees the full details there.
 */
import { CartLinesUpdateEvent } from '@shopify/events';

const STEPS = ['brewer', 'size', 'results'];
const ARROW_KEYS = new Set(['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight']);
const ADDED_RESET_MS = 4000;

/** @param {string} value */
const escapeHtml = (value) =>
  String(value ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c] || c);

/**
 * Shopify's `t` filter returns HTML-escaped text ("Couldn&#39;t"); the JSON
 * island carries it as-is, so decode before it lands in textContent.
 * @param {string} value
 */
const decodeEntities = (value) => {
  if (!value.includes('&')) return value;
  const doc = new DOMParser().parseFromString(`<!doctype html><body>${value}`, 'text/html');
  return doc.body.textContent || '';
};

/** @param {string} template @param {Record<string, string | number>} values */
const fill = (template, values) => String(template || '').replace(/\{(\w+)\}/g, (m, key) => (key in values ? String(values[key]) : m));

/** URL slug for a size tag value: "#4" -> "4", "6-10 Cup" -> "6-10-cup". */
const sizeSlug = (value) =>
  String(value)
    .toLowerCase()
    .replace(/#/g, '')
    .replace(/[^a-z0-9-]+/g, '-')
    .replace(/^-+|-+$/g, '');

/** Lower-case alphanumerics only, for comparing option values with size tags. */
const norm = (value) => String(value).toLowerCase().replace(/[^a-z0-9]/g, '');

/** Option values that stand for a size when the listing doesn't spell it out. */
const SIZE_ALIASES = { '01': ['s'], '02': ['m'], '155': ['s'], '185': ['m'] };

/**
 * Does a variant option value ("02", "[XF]", "1 gallon (3785ml)", "55",
 * "S/M (2-4 cups / for drippers 155&185)") stand for the chosen size label?
 * @param {string} label size as tagged ("02", "XF", "1 Gallon", "54-55mm")
 * @param {string} value variant option value
 */
const sizeMatchesOption = (label, value) => {
  const wanted = norm(label);
  const have = norm(value);
  if (!wanted || !have) return false;
  if (have === wanted) return true;
  if (wanted.length < 2) return false;
  // "1 gallon (3785ml)" starts with "1gallon".
  if (have.startsWith(wanted)) return true;
  // Whole-word match: "155&185" in a longer description.
  if (value.toLowerCase().split(/[^a-z0-9]+/).includes(wanted)) return true;
  // Diameters: option "55" against "54-55mm", "58" against "58mm".
  if (/^\d+$/.test(have)) {
    const numbers = String(label).match(/\d+/g) || [];
    if (/mm/i.test(label) && numbers.includes(have)) return true;
  }
  return false;
};

const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

class CccProductFinder extends HTMLElement {
  /** @type {AbortController | null} */
  #abort = null;
  /** @type {any} */
  #data = null;
  /** @type {Map<string, {key: string, tag: string, name: string, link: string, input: HTMLInputElement, tile: HTMLElement}>} */
  #brewers = new Map();
  /** @type {Map<string, string>} tag -> brewer key */
  #tagToBrewer = new Map();
  /** @type {any[]} */
  #products = [];
  /** @type {Map<string, {tag: string, slug: string, key: string, explain: string}>} */
  #sizeConfig = new Map();
  #state = { brewer: '', size: '', type: '' };
  #step = 'brewer';
  /** URL parameter names (set per section: brewer/size for filters). */
  #params = { brewer: 'brewer', size: 'size', type: 'type' };
  /** Toggle values: products with the toggle tag vs without it. */
  #typeWith = 'reusable';
  #typeWithout = 'paper';
  #arrowNav = false;

  connectedCallback() {
    this.#abort = new AbortController();
    const { signal } = this.#abort;

    this.#setupToggle(signal);

    try {
      const raw = this.querySelector('[data-cff-data]')?.textContent || '';
      this.#data = JSON.parse(raw);
      this.#index();
    } catch (error) {
      console.error('[product-finder] data failed to load', error);
      this.#ref('error')?.removeAttribute('hidden');
      this.#ref('bar')?.setAttribute('hidden', '');
      this.querySelector('[data-step="brewer"]')?.setAttribute('hidden', '');
      return;
    }

    this.#ref('bar')?.removeAttribute('hidden');
    this.#setupList(signal);

    this.addEventListener('keydown', this.#onKeydown, { signal });
    this.addEventListener('click', this.#onClick, { signal });
    this.addEventListener('pointerdown', () => (this.#arrowNav = false), { signal });
    this.addEventListener('change', this.#onChange, { signal });
    window.addEventListener('popstate', this.#onPopState, { signal });

    this.#readUrl();
    this.#render({ focus: false });
    this.#writeUrl('replace');
  }

  disconnectedCallback() {
    this.#abort?.abort();
    this.#abort = null;
  }

  // ------------------------------------------------------ hide / show toggle

  /** localStorage key; one per finder type so filters and burrs stay separate. */
  get #storageKey() {
    return `ccc-finder-collapsed:${this.dataset.finder || 'filters'}`;
  }

  /**
   * "Hide" / "Show" in the finder header. The inline script in the section
   * has already applied a remembered collapsed state before paint; this wires
   * the button, keeps aria-expanded and inert in step, and remembers the
   * choice. Open is the default.
   * @param {AbortSignal} signal
   */
  #setupToggle(signal) {
    const button = /** @type {HTMLButtonElement | null} */ (this.querySelector('[data-toggle]'));
    const body = this.#ref('body');
    if (!button || !body) return;
    button.hidden = false;
    this.#syncToggle(this.classList.contains('is-collapsed'));

    button.addEventListener(
      'click',
      () => {
        const collapse = !this.classList.contains('is-collapsed');
        if (!reducedMotion()) {
          this.classList.add('is-animating');
          const done = () => this.classList.remove('is-animating');
          body.addEventListener('transitionend', done, { once: true });
          window.setTimeout(done, 500);
        }
        this.classList.toggle('is-collapsed', collapse);
        this.#syncToggle(collapse);
        try {
          // '0' is kept too: phones start collapsed unless the visitor opened it.
          localStorage.setItem(this.#storageKey, collapse ? '1' : '0');
        } catch (error) {
          // Private mode or blocked storage: the toggle still works for this visit.
        }
      },
      { signal }
    );
  }

  /** @param {boolean} collapsed */
  #syncToggle(collapsed) {
    const button = /** @type {HTMLElement | null} */ (this.querySelector('[data-toggle]'));
    const body = this.#ref('body');
    const text = this.querySelector('[data-toggle-text]');
    if (!button || !body) return;
    button.setAttribute('aria-expanded', String(!collapsed));
    if (text) text.textContent = (collapsed ? button.dataset.labelShow : button.dataset.labelHide) || text.textContent;
    // inert takes the hidden steps out of the tab order and the a11y tree.
    body.toggleAttribute('inert', collapsed);
  }

  // ------------------------------------------------- searchable step 1 list

  /** Active brand chip ('' = all brands). */
  /** Active brand chip: null = nothing picked yet (rows hidden), '' = All brands. */
  #brand = null;

  /**
   * The "Searchable list" layout (burr chooser): a type-ahead box and brand
   * chips that narrow the step 1 rows. Rows stay native radios, so the
   * keyboard and screen reader behaviour is the same as the tiles.
   * @param {AbortSignal} signal
   */
  #setupList(signal) {
    const tools = this.#ref('list-tools');
    const brands = this.#ref('brands');
    const search = /** @type {HTMLInputElement | null} */ (this.querySelector('[data-search]'));
    if (!tools || !brands || !search) return;

    // Brand chips, with the number of models behind each.
    const counts = new Map();
    for (const tile of this.querySelectorAll('.cff-tile[data-group]:not([data-empty])')) {
      const brand = /** @type {HTMLElement} */ (tile).dataset.group || '';
      counts.set(brand, (counts.get(brand) || 0) + 1);
    }
    const chip = (value, label, count) =>
      `<button type="button" class="cff__brand" data-brand="${escapeHtml(value)}" aria-pressed="false">${escapeHtml(label)}${
        count ? ` <span class="cff__brand-count">${count}</span>` : ''
      }</button>`;
    brands.innerHTML = [
      chip('', this.#t('brandAll'), 0),
      ...[...counts.keys()].sort((a, b) => a.localeCompare(b)).map((brand) => chip(brand, brand, counts.get(brand))),
    ].join('');

    // Brand headings between the rows (decorative; each row names its brand).
    let last = '';
    for (const tile of this.querySelectorAll('.cff-tile[data-group]:not([data-empty])')) {
      const brand = /** @type {HTMLElement} */ (tile).dataset.group || '';
      if (brand !== last) {
        const head = document.createElement('p');
        head.className = 'cff-tile-group';
        head.dataset.groupHead = brand;
        head.setAttribute('aria-hidden', 'true');
        head.textContent = brand;
        tile.before(head);
        last = brand;
      }
    }

    tools.hidden = false;
    // Typing searches every brand, so it clears any picked brand chip.
    search.addEventListener(
      'input',
      () => {
        this.#setBrand(null);
        this.#filterList();
      },
      { signal }
    );
    search.addEventListener(
      'keydown',
      (event) => {
        if (event.key !== 'Enter') return;
        event.preventDefault();
        // Enter picks the only match.
        const visible = this.#visibleTiles();
        if (visible.length === 1) this.#choose(/** @type {HTMLInputElement} */ (visible[0].querySelector('input')));
      },
      { signal }
    );
    brands.addEventListener(
      'click',
      (event) => {
        const button = /** @type {HTMLElement} */ (event.target).closest('[data-brand]');
        if (!button) return;
        // A brand chip shows that brand's grinders; it replaces any search text.
        search.value = '';
        this.#setBrand(/** @type {HTMLElement} */ (button).dataset.brand || '');
        this.#filterList();
      },
      { signal }
    );
    this.#filterList({ announce: false });
  }

  /** @param {string | null} brand null = no chip picked */
  #setBrand(brand) {
    this.#brand = brand;
    for (const chip of this.querySelectorAll('[data-brand]')) {
      chip.setAttribute('aria-pressed', String(brand !== null && /** @type {HTMLElement} */ (chip).dataset.brand === brand));
    }
  }

  #visibleTiles() {
    return [...this.querySelectorAll('.cff-tile[data-group]')].filter((tile) => !(/** @type {HTMLElement} */ (tile).hidden));
  }

  /** Applies the search text and brand chip to the step 1 rows. */
  #filterList({ announce = true } = {}) {
    const search = /** @type {HTMLInputElement | null} */ (this.querySelector('[data-search]'));
    if (!search) return;
    const tokens = search.value.toLowerCase().split(/\s+/).map(norm).filter(Boolean);
    // Back on step 1 with a grinder already chosen: open its brand.
    if (this.#brand === null && !tokens.length && this.#state.brewer) {
      const chosen = /** @type {HTMLElement | undefined} */ (this.#brewers.get(this.#state.brewer)?.tile);
      if (chosen?.dataset.group) this.#setBrand(chosen.dataset.group);
    }
    // Nothing picked and nothing typed: only the search box and brand chips show.
    const idle = this.#brand === null && !tokens.length;
    const shown = new Set();
    let count = 0;
    for (const tile of this.querySelectorAll('.cff-tile[data-group]')) {
      const el = /** @type {HTMLElement} */ (tile);
      const brand = el.dataset.group || '';
      const haystack = norm(`${brand} ${el.querySelector('input')?.dataset.name || ''}`);
      const match =
        !idle &&
        !el.hasAttribute('data-empty') &&
        (!this.#brand || brand === this.#brand) &&
        tokens.every((token) => haystack.includes(token));
      el.hidden = !match;
      if (match) {
        count += 1;
        shown.add(brand);
      }
    }
    for (const head of this.querySelectorAll('[data-group-head]')) {
      /** @type {HTMLElement} */ (head).hidden = !shown.has(/** @type {HTMLElement} */ (head).dataset.groupHead || '');
    }
    const noMatch = this.#ref('no-match');
    if (noMatch) noMatch.hidden = idle || count > 0;
    const counter = this.#ref('search-count');
    if (counter && announce) counter.textContent = idle ? '' : fill(count === 1 ? this.#t('matchOne') : this.#t('matchMany'), { count });
  }

  /** @param {string} name */
  #ref(name) {
    return /** @type {HTMLElement | null} */ (this.querySelector(`[data-${name}]`));
  }

  /** Builds the lookup tables once. */
  #index() {
    for (const tile of this.querySelectorAll('.cff-tile')) {
      const input = /** @type {HTMLInputElement} */ (tile.querySelector('input'));
      const key = input.value;
      const tag = input.dataset.tag || '';
      this.#brewers.set(key, {
        key,
        tag,
        name: input.dataset.name || key,
        link: input.dataset.link || '',
        input,
        tile: /** @type {HTMLElement} */ (tile),
      });
      if (tag) this.#tagToBrewer.set(tag, key);
    }

    for (const [tag, key, explain, scope] of this.#data.sizes || []) {
      this.#sizeConfig.set(sizeSlug(tag), { tag, slug: sizeSlug(tag), key, explain, scope: scope || [] });
    }

    const config = this.#data.config || {};
    const sizePrefix = String(config.sizePrefix || 'Filter Size:').trim();
    this.#allow = this.#parseAllow(config.allow || [], String(config.prefix || ''));
    const typeTag = String(config.typeTag || '').trim();
    this.#params = {
      brewer: config.param1 || 'brewer',
      size: config.param2 || 'size',
      type: 'type',
    };
    for (const input of this.querySelectorAll('[data-type-role]')) {
      const role = /** @type {HTMLInputElement} */ (input).dataset.typeRole;
      if (role === 'with') this.#typeWith = /** @type {HTMLInputElement} */ (input).value;
      if (role === 'without') this.#typeWithout = /** @type {HTMLInputElement} */ (input).value;
    }

    this.#products = (this.#data.products || []).map((product, order) => {
      const brewers = new Set();
      const sizes = [];
      let reusable = false;
      for (const tag of product.tags || []) {
        if (typeTag && tag === typeTag) {
          reusable = true;
        } else if (tag.startsWith(sizePrefix)) {
          const value = tag.slice(sizePrefix.length).replace(/^[\s:]+/, '').trim();
          if (value) sizes.push(value);
        } else if (this.#tagToBrewer.has(tag)) {
          brewers.add(this.#tagToBrewer.get(tag));
        }
      }
      if (brewers.size === 0) brewers.add('other');
      return { ...product, order, brewers, sizes, sizeSlugs: new Set(sizes.map(sizeSlug)), reusable };
    });

    // Tile counts; tiles with nothing behind them stay out of the way.
    for (const brewer of this.#brewers.values()) {
      const count = this.#productsFor(brewer.key).length;
      const counter = brewer.tile.querySelector('[data-tile-count]');
      if (counter) counter.textContent = fill(count === 1 ? this.#t('tileOne') : this.#t('tileMany'), { count });
      brewer.tile.hidden = count === 0;
      brewer.tile.toggleAttribute('data-empty', count === 0);
    }
  }

  /** @type {Map<string, {slug: string, label: string}[]>} brewer key -> allowed sizes */
  #allow = new Map();

  /**
   * Parses the "Brewer sizes" setting. Lines look like
   * "Fellow Aiden: Single Brew=Single brew basket, Batch Brew=Batch basket".
   * The brewer is matched to a tile by its tag name (without the prefix),
   * its display name or its URL key, ignoring case and punctuation.
   * @param {string[]} lines
   * @param {string} prefix
   */
  #parseAllow(lines, prefix) {
    const byName = new Map();
    for (const brewer of this.#brewers.values()) {
      const tagName = brewer.tag.startsWith(prefix) ? brewer.tag.slice(prefix.length) : brewer.tag;
      for (const name of [tagName, brewer.name, brewer.key]) byName.set(norm(decodeEntities(name)), brewer.key);
    }
    const allow = new Map();
    for (const raw of lines) {
      const line = decodeEntities(String(raw)).trim();
      const split = line.indexOf(':');
      if (split < 1) continue;
      const key = byName.get(norm(line.slice(0, split)));
      if (!key) continue;
      const sizes = line
        .slice(split + 1)
        .split(',')
        .map((entry) => {
          const [size, label] = entry.split('=').map((part) => part.trim());
          return size ? { slug: sizeSlug(size), label: label || size } : null;
        })
        .filter(Boolean);
      if (sizes.length) allow.set(key, sizes);
    }
    return allow;
  }

  /** @param {string} key */
  #t(key) {
    const value = this.#data?.strings?.[key] ?? this.#data?.[key];
    return decodeEntities(typeof value === 'string' ? value : '');
  }

  /** @param {string} brewer */
  #productsFor(brewer) {
    return this.#products.filter((product) => product.brewers.has(brewer));
  }

  /**
   * Sizes on offer for a brewer, in the configured order (unknown sizes A-Z after).
   * @param {string} brewer
   */
  #sizesFor(brewer) {
    const found = new Map();
    for (const product of this.#productsFor(brewer)) {
      for (const size of product.sizes) found.set(sizeSlug(size), size);
    }
    // "Brewer sizes" setting: only the listed sizes, in that order, with
    // their per-brewer labels. Products carrying none stay under "Not sure".
    const allowed = this.#allow.get(brewer);
    if (allowed) return allowed.filter(({ slug }) => found.has(slug));
    const ordered = [];
    for (const [slug, config] of this.#sizeConfig) {
      if (!found.has(slug)) continue;
      found.delete(slug);
      // Brewer-specific sizes ("Origami S" on a V60-and-Origami filter) only
      // show under their own brewer; elsewhere the product stays under "Not sure".
      if (config.scope.length && !config.scope.includes(brewer)) continue;
      ordered.push({ slug, label: this.#data.sizeLabels?.[slug] || config.tag });
    }
    const rest = [...found.entries()].sort((a, b) => a[1].localeCompare(b[1], undefined, { numeric: true }));
    for (const [slug, label] of rest) ordered.push({ slug, label });
    return ordered;
  }

  /** @param {string} brewer @param {string} slug */
  #explain(brewer, slug) {
    const config = this.#sizeConfig.get(slug);
    if (!config) return '';
    return this.#data.explain?.[`${brewer}:${config.key}`] || config.explain || '';
  }

  /** True when the brewer has two or more sizes to pick from. */
  #needsSize(brewer) {
    const sizes = this.#sizesFor(brewer);
    if (sizes.length < 2) return false;
    // Skip it too when no option would narrow the list (every product under
    // every option, e.g. burrs with no style that show for each brew choice).
    const products = this.#productsFor(brewer);
    return sizes.some(({ slug }) => products.some((product) => !product.sizeSlugs.has(slug)));
  }

  // ---------------------------------------------------------------- state

  #readUrl() {
    const params = new URLSearchParams(window.location.search);
    const brewer = (params.get(this.#params.brewer) || '').trim().toLowerCase();
    const size = params.get(this.#params.size) || '';
    const type = params.get(this.#params.type) || '';
    const known = this.#brewers.get(brewer);
    // data-empty, not hidden: the grinder search hides tiles too.
    this.#state.brewer = known && !known.tile.hasAttribute('data-empty') ? brewer : '';
    this.#state.size = '';
    // A shared link can outlive the stock behind it: keep any size it names and
    // let the results say "No filters for that combo yet" instead of guessing.
    if (this.#state.brewer && size) this.#state.size = sizeSlug(size) || '';
    this.#state.type = type && (type === this.#typeWith || type === this.#typeWithout) ? type : '';
    this.#step = this.#stepForState();
  }

  /** The furthest step the current state allows. */
  #stepForState() {
    if (!this.#state.brewer) return 'brewer';
    if (this.#needsSize(this.#state.brewer) && !this.#state.size) return 'size';
    return 'results';
  }

  /** @param {'push' | 'replace'} mode */
  #writeUrl(mode) {
    const url = new URL(window.location.href);
    const set = (key, value) => (value ? url.searchParams.set(key, value) : url.searchParams.delete(key));
    set(this.#params.brewer, this.#state.brewer);
    set(this.#params.size, this.#step === 'brewer' ? '' : this.#state.size);
    set(this.#params.type, this.#step === 'results' ? this.#state.type : '');
    const next = url.pathname + url.search + url.hash;
    const current = window.location.pathname + window.location.search + window.location.hash;
    const historyState = { ...(history.state || {}), cffStep: this.#step };
    if (mode === 'push' && next !== current) {
      history.pushState(historyState, '', next);
    } else {
      history.replaceState(historyState, '', next);
    }
  }

  #onPopState = () => {
    this.#readUrl();
    const stored = history.state?.cffStep;
    if (stored && STEPS.indexOf(stored) < STEPS.indexOf(this.#step)) this.#step = stored;
    this.#render({ focus: true });
  };

  /**
   * @param {string} step
   * @param {{ push?: boolean }} [options]
   */
  #goTo(step, { push = true } = {}) {
    this.#step = step;
    this.#render({ focus: true });
    if (push) this.#writeUrl('push');
  }

  // --------------------------------------------------------------- events

  #onKeydown = (/** @type {KeyboardEvent} */ event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    if (!(target instanceof HTMLInputElement) || target.type !== 'radio') return;
    if (target.closest('[data-types]')) return;
    if (ARROW_KEYS.has(event.key)) {
      // Arrow keys move the selection inside the group; they don't advance.
      this.#arrowNav = true;
      return;
    }
    if ((event.key === 'Enter' || event.key === ' ') && target.checked) {
      event.preventDefault();
      this.#choose(target);
    }
  };

  #onClick = (/** @type {MouseEvent} */ event) => {
    const target = /** @type {HTMLElement} */ (event.target);

    if (target instanceof HTMLInputElement && target.type === 'radio') {
      if (target.closest('[data-types]')) return;
      if (this.#arrowNav) {
        this.#arrowNav = false;
        return;
      }
      this.#choose(target);
      return;
    }

    const goto = /** @type {HTMLButtonElement | null} */ (target.closest('[data-goto]'));
    if (goto && !goto.disabled) {
      const step = goto.dataset.goto || 'brewer';
      if (step !== this.#step) this.#goTo(step);
      return;
    }

    if (target.closest('[data-restart]')) {
      this.#state = { brewer: '', size: '', type: '' };
      for (const brewer of this.#brewers.values()) brewer.input.checked = false;
      this.#goTo('brewer');
      return;
    }

    const add = /** @type {HTMLButtonElement | null} */ (target.closest('[data-add]'));
    if (add) {
      event.preventDefault();
      this.#addToCart(add);
    }
  };

  #onChange = (/** @type {Event} */ event) => {
    const target = /** @type {HTMLInputElement} */ (event.target);
    if (!target.closest('[data-types]')) return;
    this.#state.type = target.value;
    this.#renderResults();
    this.#announceCount();
    this.#writeUrl('replace');
  };

  /** A brewer or size radio was picked (click, Space or Enter). */
  #choose(input) {
    if (input.closest('[data-tiles]')) {
      const brewer = input.value;
      if (brewer !== this.#state.brewer) {
        this.#state.size = '';
        this.#state.type = '';
      }
      this.#state.brewer = brewer;
      if (!this.#needsSize(brewer)) this.#state.size = '';
      this.#goTo(this.#needsSize(brewer) ? 'size' : 'results');
      return;
    }
    if (input.closest('[data-sizes]')) {
      this.#state.size = input.value;
      this.#state.type = '';
      this.#goTo('results');
    }
  }

  // --------------------------------------------------------------- render

  /** @param {{ focus: boolean }} options */
  #render({ focus }) {
    const { brewer } = this.#state;
    const brewerInfo = this.#brewers.get(brewer);

    for (const info of this.#brewers.values()) info.input.checked = info.key === brewer;
    if (this.#step === 'brewer') this.#filterList({ announce: false });
    this.#applyStepTwoCopy(brewer);

    if (this.#step !== 'brewer' && brewer) this.#renderSizes();
    if (this.#step === 'results') this.#renderResults();

    for (const section of this.querySelectorAll('[data-step]')) {
      const isCurrent = /** @type {HTMLElement} */ (section).dataset.step === this.#step;
      section.toggleAttribute('hidden', !isCurrent);
      if (isCurrent && focus && !reducedMotion()) {
        section.classList.remove('is-entering');
        void (/** @type {HTMLElement} */ (section).offsetWidth);
        section.classList.add('is-entering');
      }
    }

    this.#renderProgress(brewerInfo);

    if (focus) {
      const heading = /** @type {HTMLElement | null} */ (
        this.querySelector(`[data-step="${this.#step}"] [data-step-title]`)
      );
      heading?.focus({ preventScroll: true });
      const current = this.querySelector(`[data-step="${this.#step}"]`);
      const top = current ? current.getBoundingClientRect().top : 0;
      if (current && (top < 80 || top > window.innerHeight * 0.6)) {
        current.scrollIntoView({ block: 'start', behavior: reducedMotion() ? 'auto' : 'smooth' });
      }
    }
    if (this.#step === 'results') this.#announceCount();
  }

  /** @param {{ name: string } | undefined} brewerInfo */
  #renderProgress(brewerInfo) {
    const { brewer, size } = this.#state;
    const needsSize = brewer ? this.#needsSize(brewer) : true;
    const sizeLabel = this.#sizeLabel(size);
    const currentIndex = STEPS.indexOf(this.#step);

    for (const item of this.querySelectorAll('[data-pstep]')) {
      const step = /** @type {HTMLElement} */ (item).dataset.pstep || '';
      const index = STEPS.indexOf(step);
      const button = /** @type {HTMLButtonElement} */ (item.querySelector('button'));
      const value = /** @type {HTMLElement} */ (item.querySelector('[data-pvalue]'));
      let text = '';
      let enabled = false;
      let label = '';

      if (step === 'brewer') {
        text = brewerInfo?.name || '';
        enabled = this.#step !== 'brewer';
        if (text) label = fill(this.#t('changeBrewer'), { value: text });
      } else if (step === 'size') {
        text = brewer && needsSize ? sizeLabel : '';
        enabled = Boolean(brewer) && needsSize && this.#step !== 'size';
        if (text) label = fill(this.#t('changeSize'), { value: text });
        item.toggleAttribute('hidden', Boolean(brewer) && !needsSize);
      } else {
        enabled = false;
      }

      value.textContent = text;
      button.disabled = !enabled;
      if (label) button.setAttribute('aria-label', label);
      else button.removeAttribute('aria-label');
      item.classList.toggle('is-current', index === currentIndex);
      item.classList.toggle('is-done', index < currentIndex);
      if (index === currentIndex) button.setAttribute('aria-current', 'step');
      else button.removeAttribute('aria-current');
    }

    this.#ref('restart')?.toggleAttribute('hidden', !brewer);
  }

  /** @param {string} slug */
  #sizeLabel(slug) {
    if (!slug) return '';
    if (slug === 'any') return this.#t('sizeAny');
    const entry = this.#sizesFor(this.#state.brewer).find((size) => size.slug === slug);
    return entry ? entry.label : this.#sizeConfig.get(slug)?.tag || slug;
  }

  #renderSizes() {
    const container = this.#ref('sizes');
    if (!container) return;
    const brewer = this.#state.brewer;
    this.#applyStepTwoCopy(brewer);
    const sizes = this.#sizesFor(brewer);
    const group = `cff-size-${this.dataset.sectionId}`;
    const options = [
      ...sizes.map((size) => ({ value: size.slug, label: size.label, hint: this.#explain(brewer, size.slug) })),
      { value: 'any', label: this.#t('sizeAny'), hint: this.#t('sizeAnyHint'), any: true },
    ];

    container.innerHTML = options
      .map((option) => {
        const id = `${group}-${option.value}`;
        const count = option.any
          ? this.#productsFor(brewer).length
          : this.#productsFor(brewer).filter((product) => product.sizeSlugs.has(option.value)).length;
        const checked = option.value === this.#state.size ? ' checked' : '';
        return `<div class="cff-size${option.any ? ' cff-size--any' : ''}">
  <input class="cff-size__input" type="radio" name="${group}" id="${id}" value="${escapeHtml(option.value)}"${checked}${option.hint ? ` aria-describedby="${id}-hint"` : ''}>
  <label class="cff-size__label" for="${id}">
    <span class="cff-size__name">${escapeHtml(option.label)}</span>
    ${option.hint ? `<span class="cff-size__hint" id="${id}-hint">${escapeHtml(option.hint)}</span>` : ''}
    <span class="cff-size__count">${escapeHtml(fill(count === 1 ? this.#t('tileOne') : this.#t('tileMany'), { count }))}</span>
  </label>
</div>`;
      })
      .join('');
  }

  /** @type {{ heading: string, hint: string, progress: string } | null} */
  #stepTwoDefaults = null;

  /**
   * Some tiles word step 2 differently (espresso papers: "Paper diameter").
   * @param {string} brewer
   */
  #applyStepTwoCopy(brewer) {
    const heading = this.querySelector('[data-step="size"] [data-step-title]');
    const hint = this.querySelector('[data-step="size"] .cff__step-hint');
    const progress = this.querySelector('[data-pstep="size"] .cff__plabel');
    if (!heading || !hint || !progress) return;
    if (!this.#stepTwoDefaults) {
      this.#stepTwoDefaults = {
        heading: heading.textContent || '',
        hint: hint.textContent || '',
        progress: progress.textContent || '',
      };
    }
    const override = this.#data.stepTwo?.[brewer] || {};
    heading.textContent = decodeEntities(override.heading || '') || this.#stepTwoDefaults.heading;
    hint.textContent = decodeEntities(override.hint || '') || this.#stepTwoDefaults.hint;
    progress.textContent = decodeEntities(override.progress || '') || this.#stepTwoDefaults.progress;
  }

  /** Products for the current brewer + size, before the paper/reusable toggle. */
  #baseResults() {
    const { brewer, size } = this.#state;
    let list = this.#productsFor(brewer);
    if (size && size !== 'any') list = list.filter((product) => product.sizeSlugs.has(size));
    return list;
  }

  #renderResults() {
    const grid = this.#ref('grid');
    const empty = this.#ref('empty');
    const types = this.#ref('types');
    if (!grid || !empty) return;

    const { brewer, size } = this.#state;
    const brewerInfo = this.#brewers.get(brewer);
    const base = this.#baseResults();
    // The toggle is optional (no toggle tag set, e.g. the burr chooser).
    const hasPaper = base.some((product) => !product.reusable);
    const hasReusable = base.some((product) => product.reusable);
    const showTypes = Boolean(types) && hasPaper && hasReusable;
    if (types) types.hidden = !showTypes;
    if (!showTypes) this.#state.type = '';
    for (const input of types ? types.querySelectorAll('input') : []) {
      /** @type {HTMLInputElement} */ (input).checked = /** @type {HTMLInputElement} */ (input).value === this.#state.type;
    }

    let list = base;
    if (this.#state.type && this.#state.type === this.#typeWithout) list = list.filter((product) => !product.reusable);
    if (this.#state.type && this.#state.type === this.#typeWith) list = list.filter((product) => product.reusable);

    const cards = list
      .map((product) => this.#cardModel(product))
      .sort((a, b) => a.rank - b.rank || a.product.order - b.product.order);

    const sub = this.#ref('results-sub');
    if (sub) {
      const sizeLabel = size && size !== 'any' && this.#needsSize(brewer) ? this.#sizeLabel(size) : '';
      // A brewer-specific label ("Batch basket") already says what it is.
      const named = sizeLabel && this.#allow.get(brewer)?.some((entry) => entry.slug === size && entry.label !== this.#sizeConfig.get(size)?.tag);
      sub.textContent = sizeLabel
        ? fill(this.#t(named ? 'resultsForNamed' : 'resultsForSize'), { brewer: brewerInfo?.name || '', size: sizeLabel })
        : fill(this.#t('resultsFor'), { brewer: brewerInfo?.name || '' });
    }

    grid.innerHTML = cards.map((card) => this.#cardHtml(card)).join('');
    grid.hidden = cards.length === 0;
    empty.hidden = cards.length > 0;

    const emptyLink = /** @type {HTMLAnchorElement | null} */ (this.querySelector('[data-empty-link]'));
    if (emptyLink && brewerInfo) {
      emptyLink.textContent = fill(this.#t('emptyLink'), { brewer: brewerInfo.name });
      if (brewerInfo.link) emptyLink.href = brewerInfo.link;
    }

    const count = this.#ref('count');
    // The empty state says it in words, so no "0 filters fit" above it.
    if (count) count.textContent = cards.length ? this.#countText(cards.length) : '';
    this.#lastCount = cards.length;

    // The burr cut picker (assets/custom-burr-picker.js) narrows these down.
    this.dispatchEvent(
      new CustomEvent('cff:results', { bubbles: true, detail: { products: list, brewer, size, brewerName: brewerInfo?.name || '' } })
    );
  }

  #lastCount = 0;

  /** @param {number} count */
  #countText(count) {
    return fill(count === 1 ? this.#t('countOne') : this.#t('countMany'), { count });
  }

  #announceCount() {
    const live = this.#ref('live');
    if (!live) return;
    const sub = this.#ref('results-sub')?.textContent || '';
    const emptyTitle = this.querySelector('.cff__empty-title')?.textContent?.trim() || '';
    live.textContent = '';
    if (this.#lastCount === 0 && emptyTitle) {
      window.setTimeout(() => {
        live.textContent = `${emptyTitle}. ${sub}`;
      }, 60);
      return;
    }
    // A fresh text node after a tick makes screen readers announce repeats too.
    window.setTimeout(() => {
      live.textContent = `${this.#countText(this.#lastCount)}. ${sub}`;
    }, 60);
  }

  /**
   * Works out which variant the card shows, its stock state and its action.
   * @param {any} product
   */
  #cardModel(product) {
    const { size } = this.#state;
    const variants = product.v || [];
    let candidates = variants;
    let sizeMatched = false;

    if (size && size !== 'any' && product.so >= 0) {
      // Match on the size as tagged ("Single Brew"), not a per-brewer display label.
      const label = this.#sizeConfig.get(size)?.tag || this.#sizeLabel(size);
      const wanted = norm(label);
      const valueOf = (variant) => String(variant.o?.[product.so] ?? '');
      let matches = variants.filter((variant) => sizeMatchesOption(label, valueOf(variant)));
      if (matches.length === 0) {
        // SIBARIST names sizes S / M on the listing: S = 01 or 155, M = 02 or 185.
        const aliases = SIZE_ALIASES[wanted] || [];
        matches = variants.filter((variant) => aliases.includes(norm(valueOf(variant))));
      }
      if (matches.length > 0) {
        candidates = matches;
        sizeMatched = true;
      }
    }

    const best =
      candidates.find((variant) => variant.a && !variant.pre) ||
      candidates.find((variant) => variant.a) ||
      candidates[0] ||
      null;

    const exact = variants.length === 1 || (sizeMatched && candidates.length === 1);
    const anyInStock = candidates.some((variant) => variant.a && !variant.pre);
    const anyPre = candidates.some((variant) => variant.a && variant.pre);
    const state = anyInStock ? 'in' : anyPre ? 'pre' : 'sold';
    const rank = state === 'in' ? 0 : state === 'pre' ? 1 : 2;

    let url = product.u;
    if (best && variants.length > 1) url = `${product.u}?variant=${best.id}`;

    const priceVaries = !exact && new Set(candidates.map((variant) => variant.p)).size > 1;
    const price = exact && best ? best.p : priceVaries ? fill(this.#t('fromPrice'), { price: this.#minPrice(candidates) }) : best?.p || product.p;
    const variantLabel = best && variants.length > 1 && (exact || sizeMatched) ? best.t : '';

    let action = 'view';
    if (state === 'in' && exact) action = 'add';
    else if (state === 'in') action = 'options';
    else if (state === 'pre') action = 'pre';

    return { product, best, state, rank, url, price, variantLabel, action };
  }

  /** Cheapest formatted price among variants (prices are pre-formatted, so compare digits). */
  #minPrice(variants) {
    let best = variants[0]?.p || '';
    let bestValue = Infinity;
    for (const variant of variants) {
      const value = parseFloat(String(variant.p).replace(/[^0-9.]/g, ''));
      if (value < bestValue) {
        bestValue = value;
        best = variant.p;
      }
    }
    return best;
  }

  /**
   * Chips from the card chips metafield (burr coating: "Red Speed" reads "Red").
   * @param {any} product
   */
  #badgesHtml(product) {
    const values = Array.isArray(product.b) ? product.b : [];
    if (!values.length) return '';
    const labels = this.#data.badgeLabels || {};
    const chips = values
      .map((value) => {
        const label = decodeEntities(labels[value] || value);
        return `<li class="cff-chip cff-chip--${escapeHtml(sizeSlug(value))}">${escapeHtml(label)}</li>`;
      })
      .join('');
    const name = this.#t('badgeLabel');
    return `<ul class="cff-card__chips list-unstyled" role="list"${name ? ` aria-label="${escapeHtml(name)}"` : ''}>${chips}</ul>`;
  }

  /** @param {any} card */
  #cardHtml(card) {
    const { product, best, state, url, price, variantLabel, action } = card;
    const title = escapeHtml(product.t);
    // Cards show the short menu name; the link keeps the full product title in
    // its title and accessible name (short name first, so speech input that
    // says the visible words still matches).
    const shortName = product.n || product.t;
    const nameAttrs =
      shortName === product.t ? '' : ` title="${title}" aria-label="${escapeHtml(`${shortName}, ${product.t}`)}"`;
    const stockText = state === 'in' ? this.#t('inStock') : state === 'pre' ? this.#t('preorder') : this.#t('soldOut');
    // Show the matched size's own photo when it has one (Hario 01 vs 02 boxes).
    const src = (variantLabel && best?.i) || product.img;
    const img = src
      ? `<img class="cff-card__img" src="${escapeHtml(src)}" srcset="${escapeHtml(src.replace(/width=\d+/, 'width=240'))} 240w, ${escapeHtml(src)} 480w" sizes="(min-width: 1100px) 300px, (min-width: 750px) 30vw, 45vw" width="480" height="480" loading="lazy" alt="">`
      : '<span class="cff-card__img cff-card__img--blank"></span>';

    let actionHtml = '';
    if (action === 'add' && best) {
      actionHtml = `<button type="button" class="cff__btn cff__btn--primary cff-card__action" data-add="${best.id}" aria-label="${escapeHtml(fill(this.#t('addToCartLabel'), { title: product.t }))}">
  <span class="cff-card__action-text">${escapeHtml(this.#t('addToCart'))}</span>
</button>`;
    } else {
      const text = action === 'options' ? this.#t('chooseOptions') : action === 'pre' ? this.#t('viewPreorder') : this.#t('viewProduct');
      actionHtml = `<a class="cff__btn cff-card__action" href="${escapeHtml(url)}">${escapeHtml(text)}<span class="visually-hidden">: ${title}</span></a>`;
    }

    return `<li class="cff-card cff-card--${state}">
  <article class="cff-card__inner">
    <a class="cff-card__media" href="${escapeHtml(url)}" tabindex="-1" aria-hidden="true">${img}</a>
    <div class="cff-card__body">
      <h4 class="cff-card__title"><a class="cff-card__link" href="${escapeHtml(url)}"${nameAttrs}>${escapeHtml(shortName)}</a></h4>
      ${variantLabel ? `<p class="cff-card__variant">${escapeHtml(variantLabel)}</p>` : ''}
      ${this.#badgesHtml(product)}
      <p class="cff-card__meta">
        <span class="cff-card__price">${escapeHtml(price)}</span>
        <span class="cff-card__stock cff-card__stock--${state}">${escapeHtml(stockText)}</span>
      </p>
      <div class="cff-card__actions">${actionHtml}<p class="cff-card__note" data-add-note hidden></p></div>
    </div>
  </article>
</li>`;
  }

  // ------------------------------------------------------------ cart add

  /**
   * Resolves the cart event after a failed add with the real cart, the way
   * Horizon's product form does, so listeners never see a half state.
   * @param {{ resolve: (value: any) => void, reject: (reason: any) => void } | null} deferred
   * @param {string} base
   */
  async #settleFailedAdd(deferred, base) {
    if (!deferred) return;
    try {
      const response = await fetch(`${base}cart.js`, { headers: { Accept: 'application/json' } });
      const cart = await response.json();
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: { items: cart.items, source: 'ccc-product-finder', itemCount: 0, didError: true },
      });
    } catch (error) {
      deferred.reject(error);
    }
  }

  /** @param {HTMLButtonElement} button */
  async #addToCart(button) {
    if (button.getAttribute('aria-busy') === 'true') return;
    const variantId = Number(button.dataset.add);
    if (!variantId) return;

    const text = button.querySelector('.cff-card__action-text');
    const note = /** @type {HTMLElement | null} */ (button.parentElement?.querySelector('[data-add-note]'));
    const live = this.#ref('live');
    button.setAttribute('aria-busy', 'true');
    button.classList.add('is-busy');
    if (text) text.textContent = this.#t('adding');
    if (note) note.hidden = true;

    const sectionIds = [...document.querySelectorAll('cart-items-component')]
      .map((element) => /** @type {HTMLElement} */ (element).dataset.sectionId)
      .filter(Boolean);

    /** @type {{ promise: Promise<any>, resolve: (value: any) => void, reject: (reason: any) => void } | null} */
    let deferred = null;
    try {
      deferred = CartLinesUpdateEvent.createPromise();
      button.dispatchEvent(
        new CartLinesUpdateEvent({
          action: 'add',
          context: 'product',
          lines: [{ merchandiseId: String(variantId), quantity: 1 }],
          promise: deferred.promise,
        })
      );
    } catch (error) {
      console.warn('[product-finder] cart event unavailable', error);
      deferred = null;
    }

    const root = (typeof Theme !== 'undefined' && Theme?.routes?.root) || '/';
    const base = root.endsWith('/') ? root : `${root}/`;

    try {
      const response = await fetch(`${base}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: [{ id: variantId, quantity: 1 }],
          ...(sectionIds.length ? { sections: sectionIds.join(',') } : {}),
        }),
      });
      const added = await response.json();
      if (!response.ok || added.status) throw new Error(added.description || added.message || 'Add to cart failed');

      const cartResponse = await fetch(`${base}cart.js`, { headers: { Accept: 'application/json' } });
      const cart = await cartResponse.json();
      deferred?.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          source: 'ccc-product-finder',
          itemCount: 1,
          sections: added.sections,
          didError: false,
        },
      });

      button.classList.remove('is-busy');
      button.classList.add('is-added');
      if (text) text.textContent = this.#t('added');
      if (note) {
        note.innerHTML = `<a href="${escapeHtml(this.#t('cartUrl') || '/cart')}">${escapeHtml(this.#t('viewCart'))}</a>`;
        note.hidden = false;
      }
      if (live) live.textContent = this.#t('added');
      window.setTimeout(() => {
        button.classList.remove('is-added');
        button.removeAttribute('aria-busy');
        if (text) text.textContent = this.#t('addToCart');
      }, ADDED_RESET_MS);
    } catch (error) {
      console.warn('[product-finder] add to cart failed', error);
      await this.#settleFailedAdd(deferred, base);
      button.classList.remove('is-busy');
      button.removeAttribute('aria-busy');
      if (text) text.textContent = this.#t('addToCart');
      const message = error instanceof Error && error.message && error.message !== 'Add to cart failed' ? error.message : this.#t('addError');
      if (note) {
        note.textContent = message;
        note.hidden = false;
      }
      if (live) live.textContent = message;
    }
  }
}

if (!customElements.get('ccc-product-finder')) {
  customElements.define('ccc-product-finder', CccProductFinder);
}
